import React, { useState, useEffect, useCallback } from 'react';
import './AnalysisBoard.css';

// Read-only 9x9 board renderer with move overlays. Kept intentionally separate
// from GameBoard (1600+ lines, click-handling, freeze-zones, etc.) — analysis
// mode has no interactivity beyond "prev/next", so a dedicated renderer is a
// tenth the code and doesn't risk regressing the interactive game.

const PIECE_IMAGES = {
  white: { king: '/assets/pieces/white-king.png', knight: '/assets/pieces/white-knight.png', pawn: '/assets/pieces/white-pawn.png', advisor: '/assets/pieces/white-advisor.png', cannon: '/assets/pieces/white-cannon.png', camel: '/assets/pieces/white-camel.png', donkey: '/assets/pieces/white-donkey.png', ram: '/assets/pieces/white-ram.png', spy: '/assets/pieces/white-spy.png' },
  black: { king: '/assets/pieces/black-king.png', knight: '/assets/pieces/black-knight.png', pawn: '/assets/pieces/black-pawn.png', advisor: '/assets/pieces/black-advisor.png', cannon: '/assets/pieces/black-cannon.png', camel: '/assets/pieces/black-camel.png', donkey: '/assets/pieces/black-donkey.png', ram: '/assets/pieces/black-ram.png', spy: '/assets/pieces/black-spy.png' },
};

const ReadOnlyBoard = ({ board, played, suggested }) => {
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const piece = board && board[r] && board[r][c];
      const isDark = (r + c) % 2 === 1;
      const isPlayedFrom = played && played.fromRow === r && played.fromCol === c;
      const isPlayedTo   = played && played.toRow   === r && played.toCol   === c;
      const isSuggFrom   = suggested && suggested.fromRow === r && suggested.fromCol === c;
      const isSuggTo     = suggested && suggested.toRow   === r && suggested.toCol   === c;
      const cls = [
        'ab-square',
        isDark ? 'ab-dark' : 'ab-light',
        isPlayedFrom && 'ab-played-from',
        isPlayedTo   && 'ab-played-to',
        isSuggFrom   && 'ab-sugg-from',
        isSuggTo     && 'ab-sugg-to',
      ].filter(Boolean).join(' ');
      cells.push(
        <div key={`${r}-${c}`} className={cls}>
          {piece && <img src={PIECE_IMAGES[piece.color][piece.type]} alt="" />}
        </div>
      );
    }
  }
  return <div className="ab-board">{cells}</div>;
};

const formatScore = (s) => {
  if (s === null || s === undefined) return '—';
  if (Math.abs(s) > 100000) return s > 0 ? '#+' : '#−';
  return (s > 0 ? '+' : '') + (s / 100).toFixed(2);
};

const moveNotation = (m) =>
  m ? `${String.fromCharCode(65 + m.fromCol)}${9 - m.fromRow}→${String.fromCharCode(65 + m.toCol)}${9 - m.toRow}` : '—';

// Chess.com-style move-quality classification. Compares each played move's
// resulting position eval against the engine's best-move eval at the same
// position — the difference is the "quality drop" (in cp, always ≥ 0).
//
// Thresholds are conservative for a 9x9 variant (fewer moves per game, so
// each mistake is more consequential); tunable if we get feedback.
const QUALITY_THRESHOLDS = {
  blunder:    300,  // dropped ≥ 3 pawns of eval
  mistake:    100,  // dropped 1-3 pawns
  inaccuracy:  50,  // dropped 0.5-1 pawn
};

function moveQuality(dropCp, matchesEngine) {
  if (matchesEngine || dropCp < 0) return 'best';      // clip negatives (engine sometimes finds better after played move)
  if (dropCp >= QUALITY_THRESHOLDS.blunder)    return 'blunder';
  if (dropCp >= QUALITY_THRESHOLDS.mistake)    return 'mistake';
  if (dropCp >= QUALITY_THRESHOLDS.inaccuracy) return 'inaccuracy';
  return 'good';
}

function computeQualityByPly(positions) {
  const out = {};   // ply → { drop, quality, color }
  for (let i = 0; i < positions.length; i++) {
    const p  = positions[i];
    const nx = positions[i + 1];
    if (!p.engineMove) continue;
    // Engine's best score at p (from p.color's POV).
    const bestScore = p.engineMove.score;
    // Score reached by played move = negation of nx's engineMove.score (which
    // is from nx.color's POV, opposite of p.color). If nx is missing (last
    // ply), we can't compute drop; skip.
    if (!nx || !nx.engineMove) continue;
    const reachedScore = -nx.engineMove.score;
    const drop = bestScore - reachedScore;
    out[p.ply] = { drop, quality: moveQuality(drop, p.matchesEngine), color: p.color };
  }
  return out;
}

function summarizeQuality(qualityByPly) {
  const summary = { white: {}, black: {} };
  const buckets = ['best', 'good', 'inaccuracy', 'mistake', 'blunder'];
  for (const c of ['white', 'black']) {
    for (const b of buckets) summary[c][b] = 0;
  }
  for (const ply of Object.keys(qualityByPly)) {
    const q = qualityByPly[ply];
    if (summary[q.color]) summary[q.color][q.quality]++;
  }
  return summary;
}

const QUALITY_LABEL = {
  best:       '✓ Best',
  good:       'Good',
  inaccuracy: '?! Inaccuracy',
  mistake:    '? Mistake',
  blunder:    '?? Blunder',
};

const AnalysisBoard = ({ gameId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [idx, setIdx] = useState(0);
  // Multi-PV alternatives, keyed by ply. Fetched on demand.
  const [altsByPly, setAltsByPly] = useState({});
  const [altsLoading, setAltsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const { API_BASE_URL } = await import('../utils/apiBase');
        const res = await fetch(`${API_BASE_URL}/games/${gameId}/analyze?time=200`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) { setData(json); setIdx(0); }
      } catch (e) {
        if (!cancelled) setError(e.message || 'analysis failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gameId]);

  const nav = useCallback((delta) => {
    if (!data) return;
    setIdx((i) => Math.max(0, Math.min(data.positions.length - 1, i + delta)));
  }, [data]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  nav(-1);
      if (e.key === 'ArrowRight') nav(+1);
      if (e.key === 'Home')       setIdx(0);
      if (e.key === 'End' && data) setIdx(data.positions.length - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav, data]);

  if (loading) return <div className="ab-container"><button onClick={onBack} className="ab-back">← Back</button><div className="ab-loading">Analyzing game… (this may take a few seconds — engine runs at each position)</div></div>;
  if (error)   return <div className="ab-container"><button onClick={onBack} className="ab-back">← Back</button><div className="ab-error">Error: {error}</div></div>;
  if (!data || data.positions.length === 0) return <div className="ab-container"><button onClick={onBack} className="ab-back">← Back</button><div className="ab-empty">No moves to analyze.</div></div>;

  const pos = data.positions[idx];
  const scoreFromWhite = pos.engineMove ? (pos.color === 'white' ? pos.engineMove.score : -pos.engineMove.score) : null;
  const alts = altsByPly[pos.ply];
  const qualityByPly = computeQualityByPly(data.positions);
  const qualitySummary = summarizeQuality(qualityByPly);
  const currentQuality = qualityByPly[pos.ply];

  const fetchAlternatives = async () => {
    if (altsLoading || alts) return;
    try {
      setAltsLoading(true);
      const token = localStorage.getItem('authToken');
      const { API_BASE_URL } = await import('../utils/apiBase');
      const r = await fetch(`${API_BASE_URL}/games/${gameId}/position/${pos.ply}/top-moves?topN=3&time=250`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setAltsByPly((prev) => ({ ...prev, [pos.ply]: j.topMoves || [] }));
    } catch (e) {
      setAltsByPly((prev) => ({ ...prev, [pos.ply]: { error: e.message || 'failed' } }));
    } finally {
      setAltsLoading(false);
    }
  };

  return (
    <div className="ab-container">
      <div className="ab-header">
        <button onClick={onBack} className="ab-back">← Back</button>
        <h1>🔍 Analysis · Game #{data.gameId}</h1>
        <button
          onClick={async () => {
            const url = `${window.location.origin}/share/game/${data.gameId}`;
            try {
              if (navigator.share) {
                await navigator.share({ title: `Ranga game #${data.gameId}`, url });
              } else {
                await navigator.clipboard.writeText(url);
                alert('Share link copied!\n\n' + url);
              }
            } catch (_) { /* user cancelled or clipboard blocked */ }
          }}
          className="ab-back"
          title="Copy share link"
          style={{ marginLeft: 'auto' }}
        >
          🔗 Share
        </button>
        <div className="ab-winner">Winner: {data.winner || '—'}</div>
      </div>

      <div className="ab-quality-summary">
        <div className="ab-qs-side">
          <div className="ab-qs-label">⚪ White</div>
          <div className="ab-qs-counts">
            <span className="ab-q-best">{qualitySummary.white.best}✓</span>
            <span className="ab-q-good">{qualitySummary.white.good} good</span>
            <span className="ab-q-inaccuracy">{qualitySummary.white.inaccuracy} ?!</span>
            <span className="ab-q-mistake">{qualitySummary.white.mistake} ?</span>
            <span className="ab-q-blunder">{qualitySummary.white.blunder} ??</span>
          </div>
        </div>
        <div className="ab-qs-side">
          <div className="ab-qs-label">⚫ Black</div>
          <div className="ab-qs-counts">
            <span className="ab-q-best">{qualitySummary.black.best}✓</span>
            <span className="ab-q-good">{qualitySummary.black.good} good</span>
            <span className="ab-q-inaccuracy">{qualitySummary.black.inaccuracy} ?!</span>
            <span className="ab-q-mistake">{qualitySummary.black.mistake} ?</span>
            <span className="ab-q-blunder">{qualitySummary.black.blunder} ??</span>
          </div>
        </div>
      </div>

      <div className="ab-body">
        <ReadOnlyBoard
          board={pos.board}
          played={pos.playedMove}
          suggested={pos.matchesEngine ? null : pos.engineMove}
        />

        <div className="ab-side">
          <div className="ab-position">
            <div className="ab-label">Position {idx + 1} / {data.positions.length}</div>
            <div className="ab-ply">Ply {pos.ply} — {pos.color} to move</div>
          </div>

          <div className="ab-eval">
            <div className="ab-eval-score">{formatScore(scoreFromWhite)}</div>
            <div className="ab-eval-note">(white's POV, centipawns)</div>
          </div>

          <div className="ab-moves">
            <div className="ab-move-row">
              <span className="ab-move-label">You played:</span>
              <span className="ab-move-val">{moveNotation(pos.playedMove)}</span>
            </div>
            <div className="ab-move-row">
              <span className="ab-move-label">Engine best:</span>
              <span className="ab-move-val">{moveNotation(pos.engineMove)}</span>
            </div>
            <div className={`ab-match ${pos.matchesEngine ? 'ab-match-yes' : 'ab-match-no'}`}>
              {pos.matchesEngine ? '✓ matches engine' : '× different from engine'}
            </div>
            {currentQuality && (
              <div className={`ab-quality ab-q-${currentQuality.quality}`}>
                {QUALITY_LABEL[currentQuality.quality]}
                {currentQuality.quality !== 'best' && currentQuality.quality !== 'good' && (
                  <span className="ab-quality-drop">
                    &nbsp;({(currentQuality.drop / 100).toFixed(2)} cp lost)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="ab-alts">
            {!alts && !altsLoading && (
              <button className="ab-alts-btn" onClick={fetchAlternatives}>
                Show top 3 alternatives (slow — evaluates each legal move)
              </button>
            )}
            {altsLoading && <div className="ab-alts-loading">Evaluating alternatives…</div>}
            {alts && alts.error && <div className="ab-alts-error">Error: {alts.error}</div>}
            {alts && Array.isArray(alts) && alts.length > 0 && (
              <div className="ab-alts-list">
                <div className="ab-alts-title">Top {alts.length} moves</div>
                {alts.map((m, i) => (
                  <div key={i} className="ab-alt-row">
                    <span className="ab-alt-rank">{i + 1}.</span>
                    <span className="ab-alt-move">{moveNotation(m)}</span>
                    <span className="ab-alt-score">{formatScore(pos.color === 'white' ? m.score : -m.score)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ab-nav">
            <button onClick={() => setIdx(0)} disabled={idx === 0}>⏮ Start</button>
            <button onClick={() => nav(-1)} disabled={idx === 0}>◀ Prev</button>
            <button onClick={() => nav(+1)} disabled={idx >= data.positions.length - 1}>Next ▶</button>
            <button onClick={() => setIdx(data.positions.length - 1)} disabled={idx >= data.positions.length - 1}>End ⏭</button>
          </div>
          <div className="ab-hint">Arrow keys ← → also navigate</div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisBoard;
